import React, {useCallback, useMemo, useState} from 'react';
import {Checkbox, Radio} from '@mui/material';
import {tid} from '../../../testUtils';
import FileTree from '../FileTree/FileTree';
import {TreeNode} from '../../../store/modelFile';
import {defaultCancelButtonProps} from '../Buttons/CancelButton';
import {defaultSubmitButtonProps} from '../Buttons/SubmitButton';
import {DialogGeneric} from '../dialog/DialogGeneric';
import {SubModelSection} from '../../router/routes';

type FilePickerDialogProps = {
  title?: string;
  files: TreeNode[];
  selectedIds?: string | string[];
  section?: SubModelSection;
  open: boolean;
  onClose: () => void;
  onSubmit: (file: FilePickerDialogProps['selectedIds']) => void;
};

const FilePickerDialog: React.FC<FilePickerDialogProps> = ({
  title,
  section,
  files,
  selectedIds,
  open,
  onClose,
  onSubmit,
}) => {
  const [selection, setSelection] = useState(selectedIds);

  const toggleSelectedFile = useCallback(
    (node: any) => {
      if (Array.isArray(selection)) {
        if (selection.includes(node.uuid)) {
          setSelection(selection.filter((uuid) => uuid !== node.uuid));
        } else {
          if (section && section === SubModelSection.DISPLAY) {
            setSelection([node.uuid]);
          } else {
            setSelection([...selection, node.uuid]);
          }
        }
      } else if (node.uuid === selection) {
        setSelection(undefined);
      } else {
        setSelection(node.uuid);
      }
    },
    [selection, section],
  );

  const renderButtons = useCallback(
    (node: TreeNode) => {
      if (node.folder) {
        return null;
      }
      const checked = Array.isArray(selection) ? selection.some((uuid) => uuid === node.uuid) : node.uuid === selection;

      if (section && section === SubModelSection.DISPLAY) {
        return [<Radio key={node.uuid} color="primary" checked={checked} {...tid('radio-option', node.uuid)} />];
      } else {
        return [<Checkbox key={node.uuid} color="primary" checked={checked} {...tid('checkbox-option', node.uuid)} />];
      }
    },
    [selection, section],
  );

  return (
    <DialogGeneric
      open={open}
      buttons={[
        {
          ...defaultCancelButtonProps,
          onClick: () => onClose(),
          children: 'Close',
          key: 'cancel',
          ...tid('btn', 'cancel', 'filePicker'),
        },
        {
          ...defaultSubmitButtonProps,
          // endIcon: <DownloadIcon />,
          children: 'OK',
          key: 'OK',
          onClick: () => onSubmit(selection),
          ...tid('btn', 'submit', 'filePicker'),
        },
      ]}
      title={title || 'Select file'}
      childrenAsText={false}
      maxWidth="sm"
      fullWidth
      PaperProps={tid('dialog-file-picker') as object}
    >
      <FileTree files={files} renderButtons={renderButtons} onRowClick={(node) => toggleSelectedFile(node)} />
    </DialogGeneric>
  );
};

export const useFilePickerDialog = (
  onSubmit: (file: FilePickerDialogProps['selectedIds']) => void,
  files: TreeNode[],
  selectedIds?: FilePickerDialogProps['selectedIds'],
  title?: string,
  section?: SubModelSection,
) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const DialogComponent = useMemo(() => {
    return isOpen ? (
      <FilePickerDialog
        files={files}
        selectedIds={selectedIds}
        title={title}
        section={section || undefined}
        onSubmit={(selection) => {
          onSubmit(selection);
          close();
        }}
        onClose={close}
        open={isOpen}
      />
    ) : null;
  }, [close, isOpen, files, selectedIds, title, section, onSubmit]);

  return {
    open,
    DialogComponent,
  };
};
